import { ElementType, Track } from '@1debit/analytics';
import { BrowserActivityType } from '@1debit/chime-schemas/typescript_web_analytics';

import { TRACKED_USER_ENTERED_ENROLLMENT } from 'consts/storageConsts';

import toSnakeCase from 'utils/converters';
import { analyticsHandler } from 'utils/eventRouter';
import once from 'utils/once';
import { IS_EMBEDDED, IS_PRELOAD } from 'utils/queryParamHelpers';
import {
  PAY_FRIENDS_TOKEN_QSP,
  REFERRAL_TOKEN_QSP,
} from 'utils/queryParamHelpers';
import { getFlowName } from 'utils/segment';
import { track } from 'utils/segment/doSegmentCalls';
import {
  getValueFromStorage,
  setValueInStorage,
} from 'utils/storage/storageHelper';

/**
 * Fire segment track event for completed section
 * @param {string} - sectionLabel
 * @param {Object} - formData
 * @param {object} - options    Contains boolean of object extraProperties
 */
export function trackSectionCompletedEvent(sectionLabel, formData, options) {
  const newOptions = options || { extraProperties: {} };
  const { extraProperties } = newOptions;
  const fields = Object.keys(formData).filter(key => key !== 'pageNumber');

  track('Enrollment Section Completed', {
    page_number: formData.pageNumber,
    page_label: sectionLabel,
    fields,
    ...extraProperties,
  });

  const { pageNumber } = formData;

  // AllowList of non-PII fields that are safe to track
  const safeNonPiiFieldsToTrack = ['card_product_name'];

  const safeNonPiiFields = safeNonPiiFieldsToTrack
    .filter(fieldName => formData[fieldName] !== undefined)
    .map(fieldName => ({
      field_name: fieldName,
      field_value: String(formData[fieldName]),
    }));

  analyticsHandler.trackEvent(Track.WebFormSubmitted, {
    unique_id: 'enrollment_section_completed',
    step_name: sectionLabel,
    form_name: 'enrollment',
    fields: safeNonPiiFields,
    item_details: {
      enrollment_section_completed: {
        page_number: pageNumber,
      },
    },
  });
}

export function trackEmbeddedCTATapEvent(
  label,
  isReactNativeWebView,
  elementContext
) {
  track('Embedded Account CTA Tapped', {
    label,
    isReactNativeWebView,
  });

  analyticsHandler.trackEvent(Track.WebItemClicked, {
    unique_id: 'enrollment_primary_cta_tapped',
    element_type: ElementType.ELEMENT_TYPE_BUTTON,
    label,
    item_details: {
      enrollment_primary_cta_tapped: {
        element_context: elementContext,
      },
    },
  });
}

export function trackLoginRedirectClickEvent(label, properties = {}) {
  track('CTA Button Tapped', {
    label,
    ...properties,
  });

  analyticsHandler.trackEvent(Track.WebItemClicked, {
    unique_id: 'already_has_active_account',
    element_type: ElementType.ELEMENT_TYPE_BUTTON,
    label: 'Login',
  });
}

/**
 * Fire segment track event for submitting the final page of enrollment
 */
export function trackEnrollmentSubmitted(bankData) {
  const { analytics } = window;
  const newBankData = bankData || {};
  if (analytics) {
    track('Enrollment Request Submitted', newBankData);
  }

  analyticsHandler.trackEvent(Track.WebFormSubmitted, {
    unique_id: 'enrollment_request_submitted',
    item_details: {
      enrollment_request_submitted: {
        bank_variant: bankData || '',
      },
    },
  });
}

/**
 * Fire segment track event for session timing out
 */
export function trackEnrollmentSessionTimedOut() {
  track('Enrollment Session Timed Out');

  analyticsHandler.trackEvent(Track.WebActionTriggered, {
    unique_id: 'enrollment_session_timed_out',
  });
}

/**
 * Fire segment track event for Optimizely events
 * @param {object} experimentData
 */
export function trackOptimizelyActivated(experimentData) {
  track('Enrollment Optimizely Activated', experimentData);
}

/**
 * Fire segment track event for Feature Bucketed
 */
export function trackFeatureEnabled(featureName, isFeatureEnabled) {
  track('Feature Bucketed', {
    featureName,
    isFeatureEnabled,
  });

  analyticsHandler.trackEvent(Track.WebActionTriggered, {
    unique_id: 'feature_bucketed',
    item_details: {
      feature_bucketed: {
        feature_name: featureName,
        is_feature_enabled: isFeatureEnabled,
      },
    },
  });
}

/**
 * Fire segment track event to record server errors and selected client side errors (has value for page_label) that are
 * displayed to the user
 */
export function trackEnrollmentErrorShown(errorMessage, pageLabel) {
  track('Enrollment Error Shown', {
    errorMessage,
    ...(!!pageLabel && { page_label: pageLabel }),
  });

  analyticsHandler.trackEvent(Track.WebErrorOccurred, {
    unique_id: 'enrollment_error_shown',
    error_message: errorMessage,
    item_details: {
      enrollment_error_shown: {
        page_label: pageLabel,
      },
    },
  });
}

export const handleTrackingInputs = fn =>
  once((event, ...args) => {
    if (
      event.type === 'blur' ||
      (event.type === 'autofill' && event.detail.autoFilled === true)
    ) {
      fn(event, ...args);
    }
  });

export const trackInput = (event, pageLabel, additionalProperties) => {
  const fieldName = event.target.name;
  const value = event.target.value;

  return trackEnrollmentFieldFilledEvent(
    toSnakeCase(pageLabel),
    toSnakeCase(fieldName),
    value,
    additionalProperties
  );
};

/**
 * Track the value of the given field
 *
 * @param {string} enrollment_section
 * @param {string} field_label
 * @param {string} value
 * @param {object} additionalProperties
 */
export function trackEnrollmentFieldFilledEvent(
  enrollment_section,
  field_label,
  value,
  additionalProperties = {}
) {
  track('Enrollment Field Filled', {
    enrollment_section,
    field_label,
    value,
    embedded: IS_EMBEDDED,
    ...additionalProperties,
  });

  analyticsHandler.trackEvent(Track.WebFieldFilled, {
    unique_id: 'enrollment_field_filled',
    label: field_label,
    value,
    item_details: {
      enrollment_field_filled: {
        enrollment_section,
      },
    },
  });
}

/**
 * Track the value of the given field
 *
 * @param {object} properties   The following tracking properties may be in the object:
 *
 *  {string} value    Typed value of the auto suggest input
 *  {string} auto_address_selected    Value of the selected suggested address
 *  {string} auto_address_position    Index of the selected suggested address
 *  {string} address_shown   Yes/no value for whether a suggestion was shown. Only passed for manual options
 */
export function trackAddressAutosuggestFieldFilledEvent(properties) {
  if (Object.prototype.hasOwnProperty.call(properties, 'value')) {
    properties.number_of_characters_entered = properties.value
      ? properties.value.length
      : 0;
  }

  track('Enrollment Field Filled', {
    field_label: 'address1',
    enrollment_section: 'address_autocomplete_smarty',
    ...properties,
  });

  const { value } = properties;

  analyticsHandler.trackEvent(Track.WebFieldFilled, {
    unique_id: 'enrollment_field_filled',
    label: 'address1',
    value,
    item_details: {
      enrollment_field_filled: {
        enrollment_section: 'address_autocomplete_smarty',
      },
    },
  });
}

export function trackAddressManualOptionSelected() {
  track('Enrollment Manual Address Option Selected');

  analyticsHandler.trackEvent(Track.WebActionTriggered, {
    unique_id: 'enrollment_manual_address_option_selected',
  });
}

export function trackInvalidUSPSAddressErrorReceived() {
  track('Enrollment Invalid USPS Address Error Shown');

  analyticsHandler.trackEvent(Track.WebErrorOccurred, {
    unique_id: 'enrollment_invalid_usps_address_error_shown',
  });
}

/**
 * Track the value of the address input field when the user clicks out of it,
 * if they haven't selected an auto-suggest option
 *
 * @param {string} field_value
 */
export function trackEnrollmentFieldUnfocused(field_value) {
  track('Enrollment Field Unfocused', {
    field_value,
    character_count: field_value ? field_value.length : 0,
  });

  analyticsHandler.trackEvent(Track.WebFieldFilled, {
    unique_id: 'enrollment_field_unfocused',
    value: field_value,
  });
}

export function trackSmartyApiError(errorMessage) {
  const newErrorMessage =
    errorMessage || 'Smarty API returned an error status.';

  track('Smarty API Error Received', {
    newErrorMessage,
  });
}

export function trackSuccessPageAttribution(formData) {
  track('New Member Enrolled', {
    category: 'Enrollment Page',
    label: 'Enrollment',
  });

  analyticsHandler.trackEvent(Track.WebActionTriggered, {
    unique_id: 'new_member_enrolled',
  });

  if (!IS_EMBEDDED) {
    track(
      'Purchase',
      {},
      e => {
        if (e?.event?.messageId) {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            segmentMessageId: e.event.messageId,
          });
        }
      },
      false,
      {
        shouldSendToFacebook: true,
      },
      formData
    );

    analyticsHandler.trackEvent(Track.WebActionTriggered, {
      unique_id: 'purchase',
      item_details: {
        purchase: {
          first_name: formData.firstName,
          last_name: formData.lastName,
          phone: formData.phone,
        },
      },
    });

    track(
      'Complete Registration',
      {},
      null,
      false,
      {
        shouldSendToFacebook: true,
      },
      formData
    );

    analyticsHandler.trackEvent(Track.WebActionTriggered, {
      unique_id: 'complete_registration',
    });
  }
}

export async function trackSuccessPageDownloadCtaTapped(
  buttonText,
  callback,
  formData
) {
  track(
    'Download CTA Tapped',
    {
      label: buttonText,
      url: window.location.href,
    },
    callback,
    false,
    { shouldSendToFacebook: true },
    formData
  );

  analyticsHandler.trackEvent(Track.WebItemClicked, {
    unique_id: 'download_cta_tapped',
    element_type: ElementType.ELEMENT_TYPE_BUTTON,
    label: buttonText,
  });
}

export function trackTermsAccepted({
  term_agreed_to,
  language_of_consent,
  links,
  terms_for,
}) {
  track('Terms Accepted', {
    term_agreed_to,
    language_of_consent,
    links,
    terms_for,
    terms_location: 'enrollment',
  });

  analyticsHandler.trackEvent(Track.WebFieldFilled, {
    unique_id: 'terms_accepted',
    label: terms_for,
    value: term_agreed_to,
    item_details: {
      terms_accepted: {
        term_agreed_to,
        language_of_consent,
        links,
        terms_for,
        terms_location: 'enrollment',
      },
    },
  });
}

export function trackTermsUnchecked({
  term_agreed_to,
  language_of_consent,
  links,
  terms_for,
}) {
  track('Terms Unchecked', {
    term_agreed_to,
    language_of_consent,
    links,
    terms_for,
    terms_location: 'enrollment',
  });

  analyticsHandler.trackEvent(Track.WebFieldFilled, {
    unique_id: 'terms_unchecked',
    item_details: {
      terms_unchecked: {
        terms: term_agreed_to,
        language_of_consent,
        links,
        terms_for,
        terms_location: 'enrollment',
      },
    },
  });
}

export function trackDay0CreditBuilderEnrollment({
  safer_credit_builder_option,
  credit_builder_enrollment_successful,
}) {
  track('Day 0 Credit Builder Enrollment', {
    safer_credit_builder_option,
    credit_builder_enrollment_successful,
  });

  analyticsHandler.trackEvent(Track.WebActionTriggered, {
    unique_id: 'day_0_credit_builder_enrollment',
    item_details: {
      day_0_credit_builder_enrollment: {
        safer_credit_builder_option,
        credit_builder_enrollment_successful,
      },
    },
  });
}

export function trackInvalidDateOfBirth(props) {
  track('Invalid Date of Birth', props);

  analyticsHandler.trackEvent(Track.WebErrorOccurred, {
    unique_id: 'invalid_date_of_birth',
    error_message: props.error,
    item_details: {
      date_of_birth: props.dateOfBirth,
      formatted_date_of_birth: props.formattedDateOfBirth,
    },
  });
}

export function trackAgreeToAllCheckBoxTapped({ selected }) {
  track('Agree To All Check Box Tapped', { is_selected: !!selected });

  analyticsHandler.trackEvent(Track.WebFieldFilled, {
    unique_id: 'agree_to_all_check_box_tapped',
    label: 'I agree to all',
    value: String(selected),
  });
}

export function trackUserEnteredEnrollmentFlow() {
  const trackFlowEntered = () => {
    if (IS_PRELOAD) return;

    const trackedUserEnteredEnrollment = getValueFromStorage(
      TRACKED_USER_ENTERED_ENROLLMENT
    );
    if (trackedUserEnteredEnrollment) return;

    const flow = getFlowName();
    track('Enrollment Flow Entered', {
      pay_friends_token: PAY_FRIENDS_TOKEN_QSP,
      referral_token: REFERRAL_TOKEN_QSP,
      flow_name: flow,
    });

    analyticsHandler.trackEvent(Track.WebFlowStatusChanged, {
      unique_id: 'enrollment_flow_entered',
      flow_name: flow,
      flow_step: '',
      step_status: 'STEP_STATUS_STARTED', // TODO replace with analytics enum when ready
      item_details: {
        enrollment_flow_entered: {
          pay_anyone_token: PAY_FRIENDS_TOKEN_QSP,
          referral_token: REFERRAL_TOKEN_QSP,
        },
      },
    });
    setValueInStorage(TRACKED_USER_ENTERED_ENROLLMENT, 'true');
  };

  // Fire event when the page is not prerendering in the background anymore
  if (document.prerendering) {
    document.addEventListener('prerenderingchange', trackFlowEntered, {
      once: true,
    });
  } else {
    trackFlowEntered();
  }
}

export function track18To30EmployedEnrollment(formData) {
  track(
    '18-30 Employed Enrollment',
    {},
    null,
    false,
    {
      shouldSendToFacebook: true,
    },
    formData
  );

  analyticsHandler.trackEvent(Track.WebActionTriggered, {
    unique_id: 'employed_in_18_to_30_age_enrolled',
    item_details: {
      employed_in_18_to_30_age_enrolled: {
        first_name: formData.firstName,
        last_name: formData.lastName,
        phone: formData.phone,
      },
    },
  });
}

export function trackLearnMoreClicks(placement, label) {
  track('Learn More Link Tapped', { placement });

  analyticsHandler.trackEvent(Track.WebItemClicked, {
    unique_id: 'learn_more_link_tapped',
    element_type: ElementType.ELEMENT_TYPE_LINK,
    label,
    item_value: placement,
  });
}

export function trackEFTTermsTapped(placement, label) {
  track('EFT Link Tapped', { placement });

  analyticsHandler.trackEvent(Track.WebItemClicked, {
    unique_id: 'eft_link_tapped',
    element_type: ElementType.ELEMENT_TYPE_LINK,
    label: label,
    item_value: placement,
  });
}

export function trackLoginLinkTapped() {
  track('Login Link Tapped');

  analyticsHandler.trackEvent(Track.WebItemClicked, {
    unique_id: 'login_link_tapped',
    element_type: ElementType.ELEMENT_TYPE_LINK,
    label: 'Log in',
  });
}

export function trackSecondaryCTATapped({
  label,
  elementType = ElementType.ELEMENT_TYPE_BUTTON,
  elementContext,
  itemType = '',
}) {
  track('Secondary CTA Tapped', {
    label,
  });

  analyticsHandler.trackEvent(Track.WebItemClicked, {
    unique_id: 'enrollment_secondary_cta_tapped',
    element_type: elementType,
    label,
    item_type: itemType,
    item_details: {
      enrollment_secondary_cta_tapped: {
        element_context: elementContext,
      },
    },
  });
}

export function trackEnrollmentSmsConsented(decision) {
  track('Enrollment SMS Opt-in Decision', {
    decision,
  });

  analyticsHandler.trackEvent(Track.WebFormSubmitted, {
    unique_id: 'enrollment_sms_opt_in_decision',
    fields: [{ field_name: 'marketing_sms_consented', field_value: decision }],
  });
}

/** Arkose events */
export function trackArkoseLoaded() {
  track('Enrollment Arkose Loaded');
}

export function trackArkoseEnforcementRun() {
  track('Enrollment Arkose Enforcement Run');

  analyticsHandler.trackEvent(Track.WebVendorActionTriggered, {
    unique_id: 'enrollment_arkose_action_triggered',
    vendor: 'VENDOR_ARKOSE', // TODO replace with analytics enum when ready
    action_type: 'VENDOR_ACTION_TYPE_ENFORCEMENT_RUN', // TODO replace with analytics enum when ready
  });
}

export function trackArkoseChallengeShown() {
  track('Enrollment Arkose Challenge Shown');
}

export function trackArkoseJSTimeout() {
  track('Enrollment Arkose JS Timeout');
}

export function trackArkoseSDKError(error) {
  track(`Enrollment Arkose SDK Error: ${error}`);
}

export function trackArkoseJSError() {
  track('Enrollment Arkose JS Error');
}

export function trackArkoseChallengeHidden() {
  track('Enrollment Arkose Challenge Hidden');
}

export function trackArkoseEnforcementComplete() {
  track('Enrollment Arkose Enforcement Complete');

  analyticsHandler.trackEvent(Track.WebVendorActionTriggered, {
    unique_id: 'enrollment_arkose_enforcement_complete',
    vendor: 'VENDOR_ARKOSE', // TODO replace with analytics enum when ready
    action_type: 'VENDOR_ACTION_TYPE_ENFORCEMENT_COMPLETE', // TODO replace with analytics enum when ready
  });
}

export function trackArkoseReset() {
  track('Enrollment Arkose Reset');
}

export function trackPrivacyPolicyLinkPresented(label) {
  track('Privacy Notice Link Presented');

  analyticsHandler.trackEvent(Track.WebItemViewed, {
    unique_id: 'privacy_notice_link_presented',
    element_type: ElementType.ELEMENT_TYPE_BUTTON,
    label,
  });
}

export function trackTimeElapsedOnPage(pageLabel, timeInMs) {
  track('Time Elapsed On Page', {
    page: pageLabel,
    time_in_ms: timeInMs,
  });

  analyticsHandler.trackEvent(Track.WebBrowserActivityTracked, {
    unique_id: 'time_elapsed_on_page',
    activity_type: BrowserActivityType.BROWSER_ACTIVITY_TYPE_TIME_ELAPSED,
    activity_value: String(timeInMs),
  });
}

export function trackTimeElapsedDuringEnrollment(timeInMs) {
  track('Time Elapsed During Enrollment', {
    time_in_ms: timeInMs,
  });

  analyticsHandler.trackEvent(Track.WebBrowserActivityTracked, {
    unique_id: 'time_elapsed_during_enrollment',
    activity_type: BrowserActivityType.BROWSER_ACTIVITY_TYPE_TIME_ELAPSED,
    activity_value: String(timeInMs),
  });
}

export function trackSsnKeystrokes(keystrokes) {
  track('SSN Keystrokes', { keystrokes });

  analyticsHandler.trackEvent(Track.WebActionTriggered, {
    unique_id: 'ssn_keystrokes',
    item_details: {
      ssn_keystrokes: {
        keystrokes,
      },
    },
  });
}

export function trackCashRewardsQualifiedEnrollment() {
  track('CR Qualified Enrollment', {}, null, false, {
    shouldSendToFacebook: true,
  });
}

/**
 * Pay Anyone events
 */
export function trackClaimWithChimePayAnyone(publicId) {
  track('Pay Anyone User Identified', {
    pay_anyone_public_id: publicId,
  });

  analyticsHandler.trackEvent(Track.WebActionTriggered, {
    unique_id: 'pay_anyone_user_identified',
    item_details: {
      pay_anyone_user_identified: {
        pay_anyone_public_id: publicId,
      },
    },
  });
}

export function trackAddressValidationErrorDisplayed(errorType) {
  track('ElementDisplayed', {
    element_type: 'ErrorModal',
    element_name: 'AddressValidationErrorModal',
    cause_of_display: errorType,
  });

  analyticsHandler.trackEvent(Track.WebItemViewed, {
    unique_id: 'address_validation_error_displayed',
    element_type: ElementType.ELEMENT_TYPE_MODAL,
    item_value: errorType,
  });
}

export function trackValidationErrorDisplayed(errorType) {
  track('ElementDisplayed', {
    element_type: 'ErrorModal',
    element_name: 'ValidationErrorModal',
    cause_of_display: errorType,
  });

  analyticsHandler.trackEvent(Track.WebItemViewed, {
    unique_id: 'validation_error_displayed',
    element_type: ElementType.ELEMENT_TYPE_MODAL,
    item_value: errorType,
  });
}

export function trackDisclosureModal({ elementName }) {
  track('ElementDisplayed', {
    event_type: 'DisclosureModalDisplayed',
    element_type: 'Modal',
    element_name: elementName,
  });

  analyticsHandler.trackEvent(Track.WebItemViewed, {
    unique_id: 'disclosure_modal_displayed',
    element_type: ElementType.ELEMENT_TYPE_MODAL,
    label: elementName,
  });
}

export function trackContentDisplayed({ elementName }) {
  track('ElementDisplayed', {
    event_type: 'ContentBlockDisplayed',
    element_type: 'Card',
    element_name: elementName,
  });
}

export function trackAddressAutocompleteSelected(
  numberOfAutosuggestionsSelected
) {
  track('DropdownOptionSelected', {
    dropdown_name: 'AddressAutocompleteDropdown',
    number_of_options_previously_selected: numberOfAutosuggestionsSelected,
  });

  analyticsHandler.trackEvent(Track.WebFieldFilled, {
    unique_id: 'dropdown_option_selected',
    item_details: {
      dropdown_option_selected: {
        dropdown_name: 'AddressAutocompleteDropdown',
        number_of_options_previously_selected: numberOfAutosuggestionsSelected,
      },
    },
  });
}

export function trackAddressValidationErrorCTATapped(errorType) {
  track('ElementTapped', {
    event_type: 'PrimaryCTATapped',
    element_type: 'Button',
    element_name: 'AddressValidationErrorPrimaryCTA',
    element_context: errorType,
  });

  analyticsHandler.trackEvent(Track.WebItemClicked, {
    unique_id: 'enrollment_primary_cta_tapped',
    element_type: ElementType.ELEMENT_TYPE_BUTTON,
    label: 'AddressValidationErrorPrimaryCTA',
    item_type: 'address_validation_element',
    item_details: {
      enrollment_primary_cta_tapped: {
        element_context: errorType,
      },
    },
  });
}

export function trackAddressValidationErrorSecondaryCTATapped(errorType) {
  track('ElementTapped', {
    event_type: 'SecondaryCTATapped',
    element_type: 'Button',
    element_name: 'AddressValidationErrorSecondaryCTA',
    element_context: errorType,
  });

  analyticsHandler.trackEvent(Track.WebItemClicked, {
    unique_id: 'enrollment_secondary_cta_tapped',
    element_type: ElementType.ELEMENT_TYPE_BUTTON,
    label: 'AddressValidationErrorSecondaryCTA',
    item_type: 'address_validation_element',
    item_details: {
      enrollment_secondary_cta_tapped: {
        element_context: errorType,
      },
    },
  });
}

export function trackPrimaryCTATapped({
  elementName,
  elementContext,
  itemType = '',
}) {
  track('ElementTapped', {
    event_type: 'PrimaryCTATapped',
    element_type: 'Button',
    element_name: elementName,
    element_context: elementContext,
  });

  analyticsHandler.trackEvent(Track.WebItemClicked, {
    unique_id: 'enrollment_primary_cta_tapped',
    element_type: ElementType.ELEMENT_TYPE_BUTTON,
    label: elementName,
    item_type: itemType,
    item_details: {
      enrollment_primary_cta_tapped: {
        element_context: elementContext,
      },
    },
  });
}

// TODO: Rename this function to `trackSecondaryCTATapped` when we deprecated the exisitng call defined earlier in this file
export function trackSecondaryButtonTapped({
  elementName,
  elementContext,
  itemType = '',
}) {
  track('ElementTapped', {
    event_type: 'SecondaryCTATapped',
    element_type: 'Button',
    element_name: elementName,
    element_context: elementContext,
  });

  analyticsHandler.trackEvent(Track.WebItemClicked, {
    unique_id: 'enrollment_secondary_cta_tapped',
    element_type: ElementType.ELEMENT_TYPE_BUTTON,
    label: elementName,
    item_type: itemType,
    item_details: {
      enrollment_secondary_cta_tapped: {
        element_context: elementContext,
      },
    },
  });
}

// TO DO: deprecate this event and remove from event standardization
export function trackGhostButtonTapped({ elementName, elementContext }) {
  track('ElementTapped', {
    event_type: 'SecondaryCTATapped',
    element_type: 'ghost_button',
    element_name: elementName,
    element_context: elementContext,
  });

  analyticsHandler.trackEvent(Track.WebItemClicked, {
    unique_id: 'enrollment_secondary_cta_tapped',
    element_type: ElementType.ELEMENT_TYPE_BUTTON,
    label: elementName,
    item_details: {
      enrollment_secondary_cta_tapped: {
        element_context: elementContext,
      },
    },
  });
}

// TO DO: deprecate this event and remove from event standardization
export function trackPrefillAttestationTapped() {
  track('ElementTapped', {
    event_type: 'PrefillAttestation',
    element_type: 'Button',
    element_name: 'looks_good',
    element_context: 'PrefillConfirmation',
  });

  analyticsHandler.trackEvent(Track.WebItemClicked, {
    unique_id: 'enrollment_primary_cta_tapped',
    element_type: ElementType.ELEMENT_TYPE_BUTTON,
    label: 'looks_good',
    item_details: {
      enrollment_primary_cta_tapped: {
        element_context: 'PrefillConfirmation',
      },
    },
  });
}

export function trackCardPickerInteraction(cardId, selectionMethod) {
  track('Card Picker Interaction', {
    card_id: cardId,
    selection_method: selectionMethod,
  });

  analyticsHandler.trackEvent(Track.WebItemClicked, {
    unique_id: 'enrollment_secondary_cta_tapped',
    element_type: ElementType.ELEMENT_TYPE_BUTTON,
    label: cardId,
    item_type: 'card_picker_element',
    item_details: {
      enrollment_secondary_cta_tapped: {
        element_context: `card_picker_element_${selectionMethod}`,
      },
    },
  });
}
